import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../contexts/AuthContext";
import { Clock, XCircle, CheckCircle, AlertCircle, Send, Music, ArrowRight } from "lucide-react";

const GENRES = [
  "House", "Techno", "Hip-Hop", "R&B", "Jazz", "Electronic",
  "Reggae", "Afrobeats", "Latin", "Rock", "Pop", "Indie", "Classical", "Other",
];

export default function Apply() {
  const { user, role, loading } = useAuth();
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [loadingApp, setLoadingApp] = useState(true);
  const [form, setForm] = useState({ bio: "", location: "", genres: [] });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!loading) {
      if (!user) { navigate("/auth"); return; }
      if (role === "creator" || role === "admin") { navigate("/dashboard"); return; }
    }
  }, [loading, user, role, navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const d = await getDoc(doc(db, "creators", user.uid));
        if (d.exists()) setApplication(d.data());
      } catch (e) { console.error(e); }
      setLoadingApp(false);
    })();
  }, [user]);

  const toggleGenre = (g) =>
    setForm((f) => ({
      ...f,
      genres: f.genres.includes(g) ? f.genres.filter((x) => x !== g) : [...f.genres, g],
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.genres.length === 0) { setError("Select at least one genre."); return; }
    setSubmitting(true);
    setError("");
    try {
      const base = (user.displayName || user.email.split("@")[0]);
      const slug = base.toLowerCase().replace(/\s+/g, "").replace(/[^a-z0-9]/g, "");
      await setDoc(
        doc(db, "creators", user.uid),
        {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || "",
          photoURL: user.photoURL || "",
          bio: form.bio,
          location: form.location,
          genres: form.genres,
          slug,
          status: "pending",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setApplication({ ...form, status: "pending" });
    } catch (err) {
      setError(err.message);
    }
    setSubmitting(false);
  };

  if (loading || loadingApp) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#00ff77] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Already approved
  if (application?.status === "approved") {
    return (
      <div className="max-w-lg mx-auto py-16 text-center space-y-4">
        <CheckCircle size={48} className="text-[#00ff77] mx-auto" />
        <h2 className="text-2xl font-black text-white">You're Approved!</h2>
        <p className="text-zinc-400 text-sm">Your creator account is active.</p>
        <Link to="/dashboard" className="inline-flex items-center gap-2 bg-[#00ff77] text-black font-bold px-6 py-2.5 rounded-full hover:bg-[#00e66b] transition-colors">
          Go to Dashboard <ArrowRight size={16} />
        </Link>
      </div>
    );
  }

  // Pending review
  if (application?.status === "pending") {
    return (
      <div data-testid="apply-pending" className="max-w-lg mx-auto py-16 text-center space-y-4">
        <Clock size={48} className="text-yellow-400 mx-auto" />
        <h2 className="text-2xl font-black text-white">Application Pending</h2>
        <p className="text-zinc-400 text-sm">
          Your creator application is under review. We'll notify you once approved.
        </p>
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-2xl p-4 text-left space-y-1.5 mt-6">
          {application.genres && application.genres.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {application.genres.map((g) => (
                <span key={g} className="text-xs bg-[#2a1a3e] text-[#B026FF] px-2 py-0.5 rounded-full">{g}</span>
              ))}
            </div>
          )}
          {application.location && (
            <p className="text-zinc-500 text-xs">{application.location}</p>
          )}
        </div>
        <p className="text-zinc-600 text-xs mt-4">
          Questions? Contact <span className="text-zinc-400">stagepassadmin@gmail.com</span>
        </p>
      </div>
    );
  }

  // Restricted
  if (application?.status === "restricted") {
    return (
      <div data-testid="apply-restricted" className="max-w-lg mx-auto py-16 text-center space-y-4">
        <XCircle size={48} className="text-red-400 mx-auto" />
        <h2 className="text-2xl font-black text-white">Account Restricted</h2>
        <p className="text-zinc-400 text-sm">
          Your creator account has been restricted. Please contact support.
        </p>
        <p className="text-zinc-600 text-xs">stagepassadmin@gmail.com</p>
      </div>
    );
  }

  // Application form
  return (
    <div data-testid="apply-page" className="max-w-lg mx-auto py-8">
      <div className="text-center mb-8">
        <div className="w-14 h-14 rounded-2xl bg-[#B026FF]/20 flex items-center justify-center mx-auto mb-4">
          <Music size={24} className="text-[#B026FF]" />
        </div>
        <h1 className="text-2xl font-black text-white mb-1">Become a Creator</h1>
        <p className="text-zinc-400 text-sm">Apply to get your creator account on StagePass.</p>
      </div>

      <div className="bg-[#111] border border-white/5 rounded-2xl p-6 space-y-5">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-zinc-400 text-xs font-semibold uppercase tracking-widest mb-2 block">Bio</label>
            <textarea
              data-testid="apply-bio"
              placeholder="Tell us about yourself and your music..."
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              rows={4}
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#B026FF]/50 resize-none"
            />
          </div>

          <div>
            <label className="text-zinc-400 text-xs font-semibold uppercase tracking-widest mb-2 block">Location</label>
            <input
              data-testid="apply-location"
              type="text"
              placeholder="City, Country"
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#B026FF]/50"
            />
          </div>

          <div>
            <label className="text-zinc-400 text-xs font-semibold uppercase tracking-widest mb-2 block">
              Genres <span className="text-red-400">*</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {GENRES.map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => toggleGenre(g)}
                  data-testid={`genre-${g.toLowerCase()}`}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                    form.genres.includes(g)
                      ? "bg-[#B026FF]/20 border-[#B026FF]/50 text-[#B026FF]"
                      : "bg-transparent border-white/10 text-zinc-400 hover:border-white/30"
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-xl p-3">
              <AlertCircle size={14} className="text-red-400" />
              <p className="text-red-400 text-xs">{error}</p>
            </div>
          )}

          <button
            data-testid="apply-submit-btn"
            type="submit"
            disabled={submitting}
            className="w-full flex items-center justify-center gap-2 bg-[#B026FF] text-white font-bold py-2.5 rounded-xl hover:bg-[#9b1ee6] transition-colors disabled:opacity-50"
          >
            <Send size={15} />
            {submitting ? "Submitting..." : "Submit Application"}
          </button>
        </form>

        <p className="text-center text-zinc-600 text-xs">
          Already have an account?{" "}
          <Link to="/auth" className="text-zinc-400 hover:text-white">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
